import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { setlistService, cifraService, grupoService, supabase } from '../lib/supabase'
import toast from 'react-hot-toast'
import ShowAoVivoModal from '../components/ShowAoVivoModal'
import { VerCifraModal } from '../components/CifraCard'
import { ShowContext } from '../App'
import { usePlano } from '../hooks/usePlano'

const TIPOS = [
  { id: 'culto', label: 'Culto', icon: '⛪' },
  { id: 'ensaio', label: 'Ensaio', icon: '🎸' },
  { id: 'show', label: 'Show', icon: '🎤' },
  { id: 'outro', label: 'Outro', icon: '📋' },
]
const CORES = ['#FF8C00','#00CFFF','#3fb950','#4A90D9','#FFD166','#E67E22']

export default function SetlistsPage() {
  const { grupoId } = useParams()
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [setlists, setSetlists] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCriar, setShowCriar] = useState(false)
  const [setlistAberto, setSetlistAberto] = useState(null)
  const [cifrasDoSetlist, setCifrasDoSetlist] = useState([])
  const [todasCifras, setTodasCifras] = useState([])
  const [showAdicionarCifra, setShowAdicionarCifra] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [form, setForm] = useState({ nome: '', descricao: '', data_evento: new Date().toISOString().split('T')[0], tipo: 'culto', cor: '#FF8C00', categoria: 'cifras' })
  const [meuPapel, setMeuPapel] = useState('membro')
  const [meuPodeShow, setMeuPodeShow] = useState(false)
  const [showAoVivo, setShowAoVivo] = useState(false)
  const [cifraSelecionada, setCifraSelecionada] = useState(null)

  const { podeIniciarShow, showsRestantes, plano } = usePlano()
  const { carregarPerfil } = useAuth()
  // Show ao vivo — estado compartilhado com GrupoLayout via context
  const showCtx = useContext(ShowContext)
  const showAtivoBanner = showCtx?.showAtivo
  const abrirViewer = () => { if (showCtx) showCtx.setShowAberto(true) }

  // Re-carrega sempre que o usuário navega para esta aba (location.key muda a cada visita)
  // Isso garante que permissões concedidas por outro usuário apareçam sem precisar reiniciar
  useEffect(() => { carregar() }, [grupoId, location.key])

  async function carregar() {
    try {
      const [data, membros] = await Promise.all([
        setlistService.listar(grupoId),
        grupoService.membrosDoGrupo(grupoId)
      ])
      setSetlists(data)
      const eu = membros.find(m => m.user_id === user?.id)
      setMeuPapel(eu?.papel || 'membro')
      setMeuPodeShow(!!eu?.pode_show)
    } catch(e) { toast.error('Erro ao carregar setlists') }
    finally { setLoading(false) }
  }

  // Incrementa contador de shows no servidor e abre o modal
  async function iniciarShow() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/stripe/incrementar-show', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      const data = await res.json()
      if (!res.ok) {
        // 403 = limite atingido ou plano insuficiente
        toast.error(data.error || 'Não foi possível iniciar o show')
        return
      }
      // Atualiza contador no contexto (UI mostra shows restantes correto)
      await carregarPerfil(user.id)
    } catch (e) {
      // Falha de rede — deixa abrir o show mesmo assim (não bloqueia por erro de rede)
      console.error('Erro ao incrementar show:', e)
    }
    setShowAoVivo(true)
  }

  async function criarSetlist() {
    if (!form.nome.trim()) { toast.error('Digite o nome'); return }
    setSalvando(true)
    try {
      const novo = await setlistService.criar({ ...form, grupo_id: grupoId, criado_por: user.id })
      setSetlists(prev => [{ ...novo, setlist_cifras: [{ count: 0 }] }, ...prev])
      setShowCriar(false)
      setForm({ nome: '', descricao: '', data_evento: new Date().toISOString().split('T')[0], tipo: 'culto', cor: '#FF8C00', categoria: 'cifras' })
      toast.success('Setlist criado!')
      abrirSetlist({ ...novo, setlist_cifras: [{ count: 0 }] })
    } catch(e) { toast.error(e.message) }
    finally { setSalvando(false) }
  }

  async function abrirSetlist(sl) {
    // Setlist de partituras → abre o módulo Classic filtrado por este setlist
    if (sl.categoria === 'partituras') {
      navigate(`/grupo/${grupoId}/classic?setlist=${sl.id}`)
      return
    }
    setSetlistAberto(sl)
    try {
      const [cifras, todas] = await Promise.all([
        setlistService.getCifras(sl.id),
        cifraService.listarCifras(grupoId, { porPagina: 100 }).then(r => r.cifras)
      ])
      setCifrasDoSetlist(cifras)
      setTodasCifras(todas)
    } catch(e) { toast.error('Erro ao carregar cifras') }
  }

  async function adicionarCifra(cifra) {
    try {
      await setlistService.adicionarCifra(setlistAberto.id, cifra.id, cifra.tom_ultima_vez || cifra.tom_original, cifrasDoSetlist.length)
      const atualizadas = await setlistService.getCifras(setlistAberto.id)
      setCifrasDoSetlist(atualizadas)
      setShowAdicionarCifra(false)
      toast.success(`"${cifra.titulo}" adicionada!`)
    } catch(e) { toast.error('Erro ao adicionar') }
  }

  async function removerCifra(cifraId) {
    try {
      await setlistService.removerCifra(setlistAberto.id, cifraId)
      setCifrasDoSetlist(prev => prev.filter(c => c.cifra_id !== cifraId))
      toast.success('Removida do setlist')
    } catch(e) { toast.error('Erro ao remover') }
  }

  async function moverCifra(idx, direcao) {
    const outro = idx + direcao
    if (outro < 0 || outro >= cifrasDoSetlist.length) return
    const novaLista = [...cifrasDoSetlist]
    ;[novaLista[idx], novaLista[outro]] = [novaLista[outro], novaLista[idx]]
    setCifrasDoSetlist(novaLista)
    try {
      await Promise.all(
        novaLista.map((item, i) => setlistService.reordenar(setlistAberto.id, item.cifra_id, i))
      )
    } catch(e) { toast.error('Erro ao reordenar') }
  }

  const fmt = (d) => new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })
  const tipoIcon = (t) => TIPOS.find(x => x.id === t)?.icon || '📋'

  async function mudarCorSetlist(novaCor) {
    try {
      await setlistService.atualizarCor(setlistAberto.id, novaCor)
      setSetlistAberto(prev => ({ ...prev, cor: novaCor }))
      setSetlists(prev => prev.map(sl => sl.id === setlistAberto.id ? { ...sl, cor: novaCor } : sl))
    } catch(e) { toast.error('Erro ao mudar cor') }
  }

  // ---- TELA INTERNA DE SETLIST ----
  if (setlistAberto) {
    const cifrasJaNo = new Set(cifrasDoSetlist.map(c => c.cifra_id))
    return (
      <div className="app-container">
        {/* Banner de show ativo dentro do setlist */}
        {showAtivoBanner && (
          <div onClick={abrirViewer}
            style={{ background:'linear-gradient(135deg,#e07800,#FF8C00)', padding:'10px 16px', display:'flex', alignItems:'center', gap:10, cursor:'pointer', flexShrink:0 }}>
            <span className="ao-vivo-dot" style={{ width:8, height:8 }} />
            <div style={{ flex:1 }}>
              <div style={{ color:'#fff', fontWeight:700, fontSize:12, fontFamily:'Montserrat,sans-serif' }}>🔴 SHOW AO VIVO em andamento</div>
              <div style={{ color:'rgba(255,255,255,0.85)', fontSize:11 }}>{showAtivoBanner.setlistNome} · música {showAtivoBanner.posicao + 1}/{showAtivoBanner.total}</div>
            </div>
            <div style={{ background:'rgba(255,255,255,0.25)', padding:'5px 12px', borderRadius:50, color:'#fff', fontSize:12, fontWeight:700 }}>Entrar ›</div>
          </div>
        )}

        {/* Header */}
        <div style={{ padding: '16px 20px', background: setlistAberto.cor + '22', borderBottom: '1px solid ' + setlistAberto.cor + '44', display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={() => setSetlistAberto(null)} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', width:36, height:36, borderRadius:'50%', fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:'Montserrat,sans-serif', fontSize:18, fontWeight:700 }}>{tipoIcon(setlistAberto.tipo)} {setlistAberto.nome}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)' }}>{fmt(setlistAberto.data_evento)}</div>
          </div>
          {/* Seletor rápido de cor */}
          <div style={{ display:'flex', gap:5, alignItems:'center' }}>
            {CORES.map(cor => (
              <div key={cor} onClick={() => mudarCorSetlist(cor)}
                style={{ width: cor===setlistAberto.cor?22:16, height: cor===setlistAberto.cor?22:16, borderRadius:'50%', background:cor, cursor:'pointer', border: cor===setlistAberto.cor?'2px solid #fff':'2px solid transparent', transition:'all 0.15s', flexShrink:0 }} />
            ))}
          </div>
          <div style={{ background:setlistAberto.cor+'33', border:'1px solid '+setlistAberto.cor+'66', borderRadius:8, padding:'4px 10px', fontSize:12, color:setlistAberto.cor, fontWeight:700 }}>
            {cifrasDoSetlist.length} músicas
          </div>
        </div>

        {/* Lista de cifras */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', paddingBottom:100 }}>
          {cifrasDoSetlist.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 0', color:'rgba(255,255,255,0.3)' }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🎵</div>
              <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>Nenhuma música ainda</div>
              <div style={{ fontSize:13 }}>Adicione músicas do feed do grupo</div>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {cifrasDoSetlist.map((item, idx) => {
                const c = item.cifras
                return (
                  <div key={item.id}
                    onClick={() => c && setCifraSelecionada({ ...c, tom_ultima_vez: item.tom_usado || c.tom_ultima_vez })}
                    style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:14, padding:'12px 14px', display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>

                    {/* Botões de reordenação */}
                    <div onClick={e => e.stopPropagation()} style={{ display:'flex', flexDirection:'column', gap:2, flexShrink:0 }}>
                      <button onClick={() => moverCifra(idx, -1)} disabled={idx === 0}
                        style={{ background:'none', border:'none', color: idx===0 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)', cursor: idx===0 ? 'default' : 'pointer', fontSize:13, lineHeight:1, padding:'2px 4px' }}>▲</button>
                      <button onClick={() => moverCifra(idx, 1)} disabled={idx === cifrasDoSetlist.length-1}
                        style={{ background:'none', border:'none', color: idx===cifrasDoSetlist.length-1 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)', cursor: idx===cifrasDoSetlist.length-1 ? 'default' : 'pointer', fontSize:13, lineHeight:1, padding:'2px 4px' }}>▼</button>
                    </div>

                    <div style={{ width:30, height:30, borderRadius:'50%', background:setlistAberto.cor+'33', border:'2px solid '+setlistAberto.cor, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Montserrat,sans-serif', fontWeight:800, fontSize:12, color:setlistAberto.cor, flexShrink:0 }}>
                      {idx+1}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:14, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c?.titulo}</div>
                      <div style={{ fontSize:12, color:'rgba(255,255,255,0.45)' }}>{c?.artista}</div>
                    </div>
                    {(item.tom_usado || c?.tom_ultima_vez) && (
                      <span style={{ background:'rgba(0,207,255,0.15)', color:'#00CFFF', border:'1px solid rgba(0,207,255,0.3)', padding:'3px 8px', borderRadius:50, fontSize:11, fontWeight:700, flexShrink:0 }}>
                        {item.tom_usado || c?.tom_ultima_vez}
                      </span>
                    )}
                    <span style={{ fontSize:13, color:'var(--brand2)', flexShrink:0 }}>👁</span>
                    <button onClick={e => { e.stopPropagation(); removerCifra(item.cifra_id) }}
                      style={{ background:'rgba(255,140,0,0.1)', border:'1px solid rgba(255,140,0,0.2)', color:'#FF8C00', width:28, height:28, borderRadius:8, cursor:'pointer', fontSize:15, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Botões fixos no rodapé */}
        <div style={{ position:'fixed', bottom:0, left:'50%', transform:'translateX(-50%)', width:'100%', maxWidth:480, padding:'12px 20px 28px', background:'rgba(15,20,40,0.97)', borderTop:'1px solid rgba(255,255,255,0.08)' }}>
          {/* Botão Iniciar Show — admin ou membro com permissão */}
          {cifrasDoSetlist.length > 0 && (meuPapel === 'admin' || meuPodeShow) && (
            podeIniciarShow ? (
              <button
                onClick={iniciarShow}
                style={{
                  width:'100%', padding:13, borderRadius:12, marginBottom:8,
                  background:'linear-gradient(135deg, #e07800, #FF8C00)',
                  border:'none', color:'#fff',
                  fontFamily:'Montserrat,sans-serif', fontSize:14, fontWeight:700,
                  cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  boxShadow:'0 4px 20px rgba(255,140,0,0.35)'
                }}>
                <span className="ao-vivo-dot" style={{ width:8, height:8 }} />
                ▶ Iniciar Show ao Vivo
                {showsRestantes !== null && (
                  <span style={{ fontSize:11, opacity:0.8, fontWeight:400 }}>({showsRestantes} restantes)</span>
                )}
              </button>
            ) : (
              <button
                onClick={() => navigate('/planos')}
                style={{
                  width:'100%', padding:13, borderRadius:12, marginBottom:8,
                  background:'rgba(255,140,0,0.1)', border:'1px solid rgba(255,140,0,0.4)',
                  color:'var(--gold)', fontFamily:'Montserrat,sans-serif', fontSize:13, fontWeight:700,
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8
                }}>
                🔒 {plano === 'free' ? 'Assine para iniciar shows' : 'Limite mensal atingido — Ver planos'}
              </button>
            )
          )}
          <button onClick={() => setShowAdicionarCifra(true)} style={{ width:'100%', padding:13, borderRadius:12, background:setlistAberto.cor, border:'none', color:'#fff', fontFamily:'Montserrat,sans-serif', fontSize:14, fontWeight:700, cursor:'pointer' }}>
            + Adicionar música do grupo
          </button>
        </div>

        {/* Modal adicionar cifra */}
        {showAdicionarCifra && (
          <div className="modal-overlay" onClick={() => setShowAdicionarCifra(false)}>
            <div className="modal-sheet" onClick={e=>e.stopPropagation()}>
              <div className="modal-handle" onClick={() => setShowAdicionarCifra(false)} />
              <div style={{ fontFamily:'Montserrat,sans-serif', fontSize:18, fontWeight:700, marginBottom:16 }}>Escolher música</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:'60vh', overflowY:'auto' }}>
                {todasCifras.filter(c => !cifrasJaNo.has(c.id)).map(c => (
                  <div key={c.id} onClick={() => adicionarCifra(c)} style={{ padding:'12px 14px', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:12, cursor:'pointer', display:'flex', alignItems:'center', gap:12, transition:'all 0.15s' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:14 }}>{c.titulo}</div>
                      <div style={{ fontSize:12, color:'rgba(255,255,255,0.45)' }}>{c.artista}</div>
                    </div>
                    {c.tom_ultima_vez && (
                      <span style={{ background:'rgba(0,207,255,0.15)', color:'#00CFFF', padding:'3px 8px', borderRadius:50, fontSize:11, fontWeight:700 }}>{c.tom_ultima_vez}</span>
                    )}
                    <span style={{ color:'var(--brand2)', fontSize:20 }}>+</span>
                  </div>
                ))}
                {todasCifras.filter(c => !cifrasJaNo.has(c.id)).length === 0 && (
                  <div style={{ textAlign:'center', padding:32, color:'rgba(255,255,255,0.3)', fontSize:14 }}>
                    Todas as cifras já estão neste setlist
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Ver cifra do setlist */}
        {cifraSelecionada && (
          <VerCifraModal cifra={cifraSelecionada} onClose={() => setCifraSelecionada(null)} modoVocal={profile?.modo_vocal || profile?.instrumento === 'Voz'} />
        )}

        {/* Show Ao Vivo — modo controller */}
        {showAoVivo && (
          <ShowAoVivoModal
            mode="controller"
            cifras={cifrasDoSetlist}
            setlistNome={setlistAberto.nome}
            grupoId={grupoId}
            userName={profile?.nome || 'Músico'}
            modoVocal={profile?.modo_vocal || profile?.instrumento === 'Voz'}
            onClose={() => setShowAoVivo(false)}
          />
        )}
      </div>
    )
  }

  // ---- LISTA DE SETLISTS ----
  return (
    <div className="app-container">
      {/* Banner de show ativo — clicável mesmo sem ver o banner flutuante */}
      {showAtivoBanner && (
        <div onClick={abrirViewer}
          style={{ background:'linear-gradient(135deg,#e07800,#FF8C00)', padding:'12px 20px', display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
          <span className="ao-vivo-dot" />
          <div style={{ flex:1 }}>
            <div style={{ color:'#fff', fontWeight:700, fontSize:13, fontFamily:'Montserrat,sans-serif' }}>🔴 Show ao vivo em andamento!</div>
            <div style={{ color:'rgba(255,255,255,0.85)', fontSize:12 }}>
              {showAtivoBanner.setlistNome} · música {showAtivoBanner.posicao + 1} de {showAtivoBanner.total}
              {showAtivoBanner.controladorNome ? ` · por ${showAtivoBanner.controladorNome}` : ''}
            </div>
          </div>
          <div style={{ background:'rgba(255,255,255,0.25)', padding:'6px 14px', borderRadius:50, color:'#fff', fontSize:13, fontWeight:700, flexShrink:0 }}>
            Entrar ›
          </div>
        </div>
      )}

      <div style={{ padding:'16px 20px 0', display:'flex', alignItems:'center', gap:12 }}>
        <button onClick={() => navigate(`/grupo/${grupoId}`)} style={{ background:'none', border:'none', color:'var(--muted)', fontSize:20, cursor:'pointer' }}>‹</button>
        <div style={{ fontFamily:'Montserrat,sans-serif', fontSize:20, fontWeight:700, flex:1 }}>📅 Setlists</div>
        <button onClick={() => setShowCriar(true)} className="btn btn-primary btn-sm">+ Novo</button>
      </div>

      <div style={{ padding:'8px 20px 4px', fontSize:13, color:'rgba(255,255,255,0.4)' }}>
        Organize as músicas de cada culto, ensaio ou show
      </div>

      <div style={{ flex:1, padding:'12px 20px', paddingBottom:80, overflowY:'auto' }}>
        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:60 }}><div className="spinner" style={{ width:32, height:32 }} /></div>
        ) : setlists.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:'rgba(255,255,255,0.3)' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📅</div>
            <div style={{ fontSize:16, fontWeight:600, color:'rgba(255,255,255,0.5)', marginBottom:8 }}>Nenhum setlist ainda</div>
            <div style={{ fontSize:13 }}>Crie um para organizar as músicas do próximo culto</div>
            <button onClick={() => setShowCriar(true)} className="btn btn-primary" style={{ marginTop:20 }}>
              + Criar primeiro setlist
            </button>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {setlists.map(sl => (
              <div key={sl.id} onClick={() => abrirSetlist(sl)} style={{ background:'rgba(255,255,255,0.04)', border:`1px solid ${sl.cor}44`, borderLeft:`4px solid ${sl.cor}`, borderRadius:14, padding:'16px', cursor:'pointer', transition:'all 0.2s' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                  <div style={{ width:44, height:44, borderRadius:12, background:sl.cor+'22', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                    {sl.categoria === 'partituras' ? '🎼' : tipoIcon(sl.tipo)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:'Montserrat,sans-serif', fontSize:16, fontWeight:700, marginBottom:2 }}>{sl.nome}</div>
                    <div style={{ fontSize:12, color:'rgba(255,255,255,0.45)', marginBottom:6, textTransform:'capitalize' }}>
                      {fmt(sl.data_evento)}
                      {sl.categoria === 'partituras' && <span style={{ color:'var(--gold)', marginLeft:6 }}>· Partituras</span>}
                    </div>
                    {sl.descricao && <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)' }}>{sl.descricao}</div>}
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ background:sl.cor+'22', border:'1px solid '+sl.cor+'44', borderRadius:8, padding:'4px 10px', fontSize:12, color:sl.cor, fontWeight:700 }}>
                      {sl.categoria === 'partituras' ? '🎼' : `${sl.setlist_cifras?.[0]?.count || 0} músicas`}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal criar setlist */}
      {showCriar && (
        <div className="modal-overlay" onClick={() => setShowCriar(false)}>
          <div className="modal-sheet" onClick={e=>e.stopPropagation()}>
            <div className="modal-handle" onClick={() => setShowCriar(false)} />
            <div style={{ fontFamily:'Montserrat,sans-serif', fontSize:20, fontWeight:700, marginBottom:4 }}>Novo Setlist</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.45)', marginBottom:14 }}>Crie uma pasta para organizar as músicas</div>

            {/* Toggle: Cifras ou Partituras */}
            <div style={{ display:'flex', background:'rgba(255,255,255,0.05)', borderRadius:12, padding:4, marginBottom:18, gap:4 }}>
              {[
                { id:'cifras', label:'🎵 Cifras', desc:'Músicas com acordes' },
                { id:'partituras', label:'🎼 Partituras', desc:'Classic / Orquestra' }
              ].map(op => (
                <button key={op.id} onClick={() => setForm(f => ({ ...f, categoria: op.id }))}
                  style={{ flex:1, padding:'10px 8px', borderRadius:9, border:'none', cursor:'pointer', transition:'all 0.2s',
                    background: form.categoria === op.id ? (op.id === 'partituras' ? 'var(--gold)' : 'var(--brand2)') : 'transparent',
                    color: form.categoria === op.id ? '#fff' : 'rgba(255,255,255,0.45)',
                    fontFamily:'Montserrat,sans-serif', fontSize:13, fontWeight:700 }}>
                  <div>{op.label}</div>
                  <div style={{ fontSize:10, fontWeight:400, opacity:0.8, marginTop:2 }}>{op.desc}</div>
                </button>
              ))}
            </div>

            <div className="input-group">
              <label className="input-label">Nome *</label>
              <input className="input" placeholder="Ex: Culto Domingo 13/04" value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} autoFocus />
            </div>

            <div style={{ display:'grid', gridTemplateColumns: form.categoria === 'partituras' ? '1fr' : '1fr 1fr', gap:10 }}>
              <div className="input-group">
                <label className="input-label">Data *</label>
                <input className="input" type="date" value={form.data_evento} onChange={e=>setForm(f=>({...f,data_evento:e.target.value}))} />
              </div>
              {form.categoria === 'cifras' && (
                <div className="input-group">
                  <label className="input-label">Tipo</label>
                  <select className="input" value={form.tipo} onChange={e=>setForm(f=>({...f,tipo:e.target.value}))} style={{appearance:'none'}}>
                    {TIPOS.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="input-group">
              <label className="input-label">Descrição (opcional)</label>
              <input className="input" placeholder="Ex: Tema: Adoração" value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} />
            </div>

            <div className="input-group" style={{ marginBottom:20 }}>
              <label className="input-label">Cor</label>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {CORES.map(cor => (
                  <div key={cor} onClick={() => setForm(f=>({...f,cor}))} style={{ width:36, height:36, borderRadius:'50%', background:cor, cursor:'pointer', border: form.cor===cor ? '3px solid #fff' : '3px solid transparent', transition:'all 0.15s' }} />
                ))}
              </div>
            </div>

            <button className="btn btn-primary btn-block" onClick={criarSetlist} disabled={salvando} style={{ height:50, fontSize:15, fontWeight:700 }}>
              {salvando ? <span className="spinner"/> : '📅 Criar Setlist'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
